/**
 * Mock data for professor's mortgage backend endpoints.
 * Replace with real API calls when backend is ready.
 */

// Simulate network delay
export const mockDelay = (ms = 800) =>
  new Promise((resolve) => setTimeout(resolve, ms));

// ─── Auth Mocks ───────────────────────────────────────────────────────────────

export const MOCK_AUTH = {
  login: async ({ email, password }) => {
    await mockDelay(600);
    if (!email || !password) {
      throw { code: 'AUTH_INVALID_CREDENTIALS', message: 'Email and password are required.' };
    }
    // Simulate wrong credentials
    if (password === 'wrong') {
      throw { code: 'AUTH_INVALID_CREDENTIALS', message: 'Invalid email or password.' };
    }
    return {
      success: true,
      token: 'mock_jwt_token_' + Date.now(),
      refreshToken: 'mock_refresh_token_' + Date.now(),
      expiresIn: 3600,
      user: {
        id: 'usr_001',
        email,
        name: 'Demo User',
        role: 'applicant',
        createdAt: new Date()?.toISOString(),
      },
    };
  },

  refresh: async ({ refreshToken }) => {
    await mockDelay(400);
    if (!refreshToken) {
      throw { code: 'AUTH_TOKEN_INVALID', message: 'Refresh token is missing or invalid.' };
    }
    return {
      success: true,
      token: 'mock_jwt_token_refreshed_' + Date.now(),
      expiresIn: 3600,
    };
  },

  logout: async () => {
    await mockDelay(300);
    return { success: true, message: 'Logged out successfully.' };
  },

  profile: async () => {
    await mockDelay(400);
    return {
      id: 'usr_001',
      email: 'demo@listinglens.com',
      name: 'Demo User',
      role: 'applicant',
      createdAt: '2024-01-15T10:00:00Z',
    };
  },
};

// ─── Mortgage Calculation Mocks ───────────────────────────────────────────────

export const MOCK_MORTGAGE = {
  eligibilityCheck: async ({ annualIncome, monthlyExpenses, deposit, propertyPrice, loanTerm = 30, creditScore = 700 }) => {
    await mockDelay(1000);

    const income = parseFloat(annualIncome) || 0;
    const expenses = parseFloat(monthlyExpenses) || 0;
    const dep = parseFloat(deposit) || 0;
    const price = parseFloat(propertyPrice) || 0;
    const term = parseInt(loanTerm) || 30;

    const monthlyIncome = income / 12;
    const dti = expenses / monthlyIncome; // Debt-to-income ratio
    const lvr = ((price - dep) / price) * 100; // Loan-to-value ratio
    const loanAmount = price - dep;

    const eligible = dti < 0.43 && lvr <= 95 && creditScore >= 620 && income >= 30000;

    return {
      success: true,
      eligible,
      eligibilityScore: eligible ? Math.min(95, Math.round(70 + creditScore / 100)) : Math.round(40 + creditScore / 100),
      summary: eligible
        ? 'You meet the basic eligibility criteria for a mortgage.'
        : 'You may not meet all eligibility criteria. See details below.',
      details: {
        debtToIncomeRatio: parseFloat(dti?.toFixed(3)),
        loanToValueRatio: parseFloat(lvr?.toFixed(2)),
        loanAmount,
        depositPercentage: parseFloat(((dep / price) * 100)?.toFixed(2)),
        creditScoreStatus: creditScore >= 740 ? 'Excellent' : creditScore >= 670 ? 'Good' : creditScore >= 580 ? 'Fair' : 'Poor',
      },
      flags: [
        ...(dti >= 0.43 ? [{ type: 'warning', message: 'Debt-to-income ratio exceeds recommended 43%.' }] : []),
        ...(lvr > 80 ? [{ type: 'info', message: 'LVR above 80% — Lenders Mortgage Insurance (LMI) may apply.' }] : []),
        ...(creditScore < 620 ? [{ type: 'error', message: 'Credit score below minimum threshold of 620.' }] : []),
      ],
      nextSteps: eligible
        ? ['Proceed to full application', 'Compare lender rates', 'Get pre-approval']
        : ['Improve credit score', 'Reduce existing debts', 'Increase deposit'],
    };
  },

  calculatePayment: async ({ loanAmount, interestRate, loanTerm, repaymentType = 'principal_and_interest' }) => {
    await mockDelay(700);

    const principal = parseFloat(loanAmount) || 0;
    const annualRate = parseFloat(interestRate) || 6.0;
    const term = parseInt(loanTerm) || 30;

    const monthlyRate = annualRate / 100 / 12;
    const numPayments = term * 12;

    let monthlyPayment;
    if (repaymentType === 'interest_only') {
      monthlyPayment = principal * monthlyRate;
    } else {
      monthlyPayment =
        principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
        (Math.pow(1 + monthlyRate, numPayments) - 1);
    }

    const totalRepayment = monthlyPayment * numPayments;
    const totalInterest = totalRepayment - principal;

    return {
      success: true,
      repaymentType,
      monthlyPayment: parseFloat(monthlyPayment?.toFixed(2)),
      fortnightlyPayment: parseFloat((monthlyPayment / 2)?.toFixed(2)),
      weeklyPayment: parseFloat((monthlyPayment / 4.33)?.toFixed(2)),
      totalRepayment: parseFloat(totalRepayment?.toFixed(2)),
      totalInterest: parseFloat(totalInterest?.toFixed(2)),
      interestRate: annualRate,
      loanTerm: term,
      loanAmount: principal,
      amortizationSchedule: Array.from({ length: Math.min(12, numPayments) }, (_, i) => {
        const balance = principal * Math.pow(1 + monthlyRate, i + 1) - monthlyPayment * ((Math.pow(1 + monthlyRate, i + 1) - 1) / monthlyRate);
        const interestPaid = principal * monthlyRate * Math.pow(1 + monthlyRate, i) - monthlyPayment * (Math.pow(1 + monthlyRate, i) - 1);
        return {
          month: i + 1,
          payment: parseFloat(monthlyPayment?.toFixed(2)),
          principal: parseFloat((monthlyPayment - interestPaid)?.toFixed(2)),
          interest: parseFloat(interestPaid?.toFixed(2)),
          balance: parseFloat(Math.max(0, balance)?.toFixed(2)),
        };
      }),
    };
  },

  applyForApproval: async ({ applicantDetails, propertyDetails, financialDetails }) => {
    await mockDelay(1500);

    if (!applicantDetails?.email || !propertyDetails?.price || !financialDetails?.annualIncome) {
      throw { code: 'VALIDATION_ERROR', message: 'Missing required application fields.', fields: ['applicantDetails.email', 'propertyDetails.price', 'financialDetails.annualIncome'] };
    }

    const applicationId = 'APP-' + Date.now();

    return {
      success: true,
      applicationId,
      status: 'submitted',
      submittedAt: new Date()?.toISOString(),
      estimatedDecisionDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)?.toISOString(),
      message: 'Your application has been submitted successfully. You will receive a decision within 3 business days.',
      trackingUrl: `/mortgage/application/${applicationId}`,
      nextSteps: [
        'Upload supporting documents (payslips, bank statements)',
        'Complete identity verification',
        'Await lender assessment',
      ],
    };
  },

  refinanceAnalysis: async ({ currentLoanBalance, currentInterestRate, currentLoanTerm, newInterestRate, newLoanTerm, propertyValue }) => {
    await mockDelay(1100);

    const balance = parseFloat(currentLoanBalance) || 0;
    const currentRate = parseFloat(currentInterestRate) || 6.5;
    const newRate = parseFloat(newInterestRate) || 5.8;
    const currentTerm = parseInt(currentLoanTerm) || 25;
    const newTerm = parseInt(newLoanTerm) || 25;

    const calcMonthly = (principal, annualRate, termYears) => {
      const r = annualRate / 100 / 12;
      const n = termYears * 12;
      return principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    };

    const currentMonthly = calcMonthly(balance, currentRate, currentTerm);
    const newMonthly = calcMonthly(balance, newRate, newTerm);
    const monthlySavings = currentMonthly - newMonthly;
    const annualSavings = monthlySavings * 12;
    const totalSavings = annualSavings * newTerm;
    const breakEvenMonths = monthlySavings > 0 ? Math.ceil(3000 / monthlySavings) : null; // Assume $3000 refinance cost

    return {
      success: true,
      recommendation: monthlySavings > 50 ? 'Refinancing is recommended' : 'Marginal benefit — consider carefully',
      currentLoan: {
        monthlyPayment: parseFloat(currentMonthly?.toFixed(2)),
        interestRate: currentRate,
        remainingTerm: currentTerm,
      },
      newLoan: {
        monthlyPayment: parseFloat(newMonthly?.toFixed(2)),
        interestRate: newRate,
        term: newTerm,
      },
      savings: {
        monthly: parseFloat(monthlySavings?.toFixed(2)),
        annual: parseFloat(annualSavings?.toFixed(2)),
        total: parseFloat(totalSavings?.toFixed(2)),
        breakEvenMonths,
      },
      lvr: propertyValue ? parseFloat(((balance / parseFloat(propertyValue)) * 100)?.toFixed(2)) : null,
      estimatedRefinanceCost: 3000,
    };
  },

  getRates: async ({ loanAmount, loanTerm = 30, loanType = 'owner_occupier' }) => {
    await mockDelay(600);
    return {
      success: true,
      fetchedAt: new Date()?.toISOString(),
      lenders: [
        { name: 'Commonwealth Bank', variableRate: 6.14, fixedRate1yr: 5.89, fixedRate3yr: 5.99, comparisonRate: 6.18, features: ['Offset account', 'Redraw facility'] },
        { name: 'Westpac', variableRate: 6.24, fixedRate1yr: 5.79, fixedRate3yr: 6.09, comparisonRate: 6.28, features: ['Offset account', 'Extra repayments'] },
        { name: 'ANZ', variableRate: 6.19, fixedRate1yr: 5.84, fixedRate3yr: 6.04, comparisonRate: 6.22, features: ['Redraw facility', 'Split loan'] },
        { name: 'NAB', variableRate: 6.09, fixedRate1yr: 5.74, fixedRate3yr: 5.94, comparisonRate: 6.13, features: ['Offset account', 'Flexible repayments'] },
        { name: 'ING', variableRate: 5.89, fixedRate1yr: 5.64, fixedRate3yr: 5.79, comparisonRate: 5.93, features: ['100% offset', 'No annual fee'] },
      ],
    };
  },

  affordabilityCheck: async ({ annualIncome, monthlyExpenses, deposit }) => {
    await mockDelay(800);

    const income = parseFloat(annualIncome) || 0;
    const expenses = parseFloat(monthlyExpenses) || 0;
    const dep = parseFloat(deposit) || 0;

    const monthlyIncome = income / 12;
    const maxMonthlyRepayment = (monthlyIncome - expenses) * 0.3;
    const interestRate = 0.06;
    const monthlyRate = interestRate / 12;
    const numPayments = 30 * 12;
    const maxLoan = maxMonthlyRepayment * ((1 - Math.pow(1 + monthlyRate, -numPayments)) / monthlyRate);
    const maxPropertyPrice = maxLoan + dep;

    return {
      success: true,
      maxBorrowingPower: parseFloat(maxLoan?.toFixed(2)),
      maxPropertyPrice: parseFloat(maxPropertyPrice?.toFixed(2)),
      maxMonthlyRepayment: parseFloat(maxMonthlyRepayment?.toFixed(2)),
      deposit: dep,
      assumptions: {
        interestRate: 6.0,
        loanTerm: 30,
        repaymentRatio: 0.3,
        note: 'Based on 30% of net disposable income at 6% p.a.',
      },
    };
  },
};

// ─── Utility Mocks ────────────────────────────────────────────────────────────

export const MOCK_UTILITY = {
  healthCheck: async () => {
    await mockDelay(200);
    return { status: 'ok', version: '1.0.0-mock', timestamp: new Date()?.toISOString() };
  },

  getLenders: async () => {
    await mockDelay(500);
    return {
      success: true,
      lenders: [
        { id: 'cba', name: 'Commonwealth Bank', type: 'major_bank', logo: null },
        { id: 'wbc', name: 'Westpac', type: 'major_bank', logo: null },
        { id: 'anz', name: 'ANZ', type: 'major_bank', logo: null },
        { id: 'nab', name: 'NAB', type: 'major_bank', logo: null },
        { id: 'ing', name: 'ING', type: 'online_lender', logo: null },
        { id: 'macquarie', name: 'Macquarie Bank', type: 'specialist', logo: null },
      ],
    };
  },

  getInterestRates: async () => {
    await mockDelay(400);
    return {
      success: true,
      rbaOfficialRate: 4.35,
      averageVariableRate: 6.14,
      averageFixedRate1yr: 5.84,
      averageFixedRate3yr: 5.97,
      lastUpdated: new Date()?.toISOString(),
    };
  },
};
